import * as Sentry from "@sentry/vue";
import {
  containsSchoolStoreSensitiveTelemetry,
  isSchoolStoreTelemetryUrl,
  isSchoolStoreRoutePath,
} from "~/utils/schoolStoreTelemetry";
import { ENDPOINTS } from "~/repository/endpoints";
import { isExpectedUnauthorizedError } from "~/repository/authSession";
import {
  containsVideoCallTelemetryReference,
  isVideoCallTelemetryUrl,
} from "~/utils/videoCalls/telemetry";
import { isVideoCallRoutePath } from "~/utils/videoCalls/privacy";

const SENTRY_BREADCRUMB_HTTP_CATEGORIES = new Set(["xhr", "fetch"]);
const VIDEO_CALL_SENSITIVE_KEYS = new Set([
  "authorization",
  "candidate",
  "guest_secret",
  "sdp",
  "share_url",
  "shareurl",
  "ticket",
  "token",
]);
const REDACTED_VALUE = "[Redacted video call data]";
const isVideoCallPrivacyActive = () =>
  (
    window as typeof window & {
      __teachplusVideoCallPrivacyActive?: boolean;
    }
  ).__teachplusVideoCallPrivacyActive === true;
const NON_RETRYABLE_401_PATHS = [
  ENDPOINTS.AUTH.REFRESH,
  ENDPOINTS.AUTH.LOGIN,
  ENDPOINTS.AUTH.STUDENT_LOGIN,
  ENDPOINTS.AUTH.CREATE_TEACHER_ACCOUNT,
  ENDPOINTS.AUTH.CREATE_STUDENT_ACCOUNT,
  ENDPOINTS.AUTH.PASSWORD_RESET,
  ENDPOINTS.AUTH.PASSWORD_RESET_VALIDATE_TOKEN,
  ENDPOINTS.AUTH.PASSWORD_RESET_CONFIRMATION,
  ENDPOINTS.CLASSROOM.PUBLIC_CLASSROOMS,
  ENDPOINTS.BRAZIL_API.CEP,
  "https://api.pagar.me/core/v5/tokens",
];

const getBreadcrumbStatusCode = (
  breadcrumb: Sentry.Breadcrumb,
): number | null => {
  const statusCode =
    breadcrumb.data?.status_code ?? breadcrumb.data?.statusCode;

  if (typeof statusCode === "number") {
    return statusCode;
  }

  if (typeof statusCode === "string") {
    return Number(statusCode);
  }

  return null;
};

const getBreadcrumbUrl = (breadcrumb: Sentry.Breadcrumb): string => {
  return typeof breadcrumb.data?.url === "string" ? breadcrumb.data.url : "";
};

const sanitizeVideoCallTelemetry = (
  value: unknown,
  seen = new WeakSet<object>(),
  redactNames = false,
): unknown => {
  if (!value || typeof value !== "object") {
    return value;
  }

  if (seen.has(value)) {
    return REDACTED_VALUE;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) =>
      sanitizeVideoCallTelemetry(item, seen, redactNames),
    );
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      VIDEO_CALL_SENSITIVE_KEYS.has(key.toLowerCase()) ||
      (redactNames &&
        ["name", "display_name", "host_name"].includes(key.toLowerCase()))
        ? REDACTED_VALUE
        : sanitizeVideoCallTelemetry(item, seen, redactNames),
    ]),
  );
};

const isExpectedUnauthorizedBreadcrumb = (
  breadcrumb: Sentry.Breadcrumb,
): boolean => {
  if (
    !breadcrumb.category ||
    !SENTRY_BREADCRUMB_HTTP_CATEGORIES.has(breadcrumb.category)
  ) {
    return false;
  }

  if (getBreadcrumbStatusCode(breadcrumb) !== 401) {
    return false;
  }

  const url = getBreadcrumbUrl(breadcrumb);

  if (!url.includes("/api/")) {
    return false;
  }

  return !NON_RETRYABLE_401_PATHS.some((path) => url.includes(path));
};

async function lazyLoadSentryIntegrations() {
  const { Replay } = await import("@sentry/vue");
  Sentry.addIntegration(
    new Replay({
      maskAllText: false,
      blockAllMedia: true,
    }),
  );
}

function getSentryIntegrations() {
  const router = useRouter();
  const browserTracing = new Sentry.BrowserTracing({
    routingInstrumentation: Sentry.vueRouterInstrumentation(router),
  });

  return [browserTracing];
}

export default defineNuxtPlugin({
  name: "sentry",
  parallel: true,
  setup(nuxtApp) {
    const vueApp = nuxtApp.vueApp;

    const config = useRuntimeConfig();

    Sentry.init({
      app: vueApp,
      dsn: config.public.SENTRY_DSN ?? undefined,
      integrations: getSentryIntegrations(),

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.1,

      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

      // This sets the sample rate. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: 0,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 1,
      beforeSend(event, hint) {
        if (
          isSchoolStoreRoutePath(window.location.pathname) ||
          containsSchoolStoreSensitiveTelemetry(event) ||
          containsSchoolStoreSensitiveTelemetry(hint.originalException)
        )
          return null;
        if (
          isVideoCallPrivacyActive() ||
          isVideoCallRoutePath(window.location.pathname)
        ) {
          return null;
        }

        if (isExpectedUnauthorizedError(hint.originalException)) {
          return null;
        }

        if (containsVideoCallTelemetryReference(event)) {
          return null;
        }

        const eventUrl = event.request?.url ?? "";
        if (event.request?.url) {
          event.request.url = event.request.url.split("#", 1)[0];
        }

        const containsVideoCallData = isVideoCallTelemetryUrl(eventUrl);
        if (containsVideoCallData && event.request) {
          event.request.data = REDACTED_VALUE;
          event.request.headers = {};
        }

        return sanitizeVideoCallTelemetry(
          event,
          new WeakSet<object>(),
          containsVideoCallData,
        ) as Sentry.Event;
      },
      beforeSendTransaction(event) {
        if (
          isSchoolStoreRoutePath(window.location.pathname) ||
          containsSchoolStoreSensitiveTelemetry(event)
        )
          return null;
        if (
          isVideoCallPrivacyActive() ||
          isVideoCallRoutePath(window.location.pathname)
        ) {
          return null;
        }

        if (containsVideoCallTelemetryReference(event)) {
          return null;
        }

        return event;
      },
      beforeBreadcrumb(breadcrumb) {
        if (
          isSchoolStoreRoutePath(window.location.pathname) ||
          isSchoolStoreTelemetryUrl(getBreadcrumbUrl(breadcrumb)) ||
          containsSchoolStoreSensitiveTelemetry(breadcrumb)
        )
          return null;
        if (
          isVideoCallPrivacyActive() ||
          isVideoCallRoutePath(window.location.pathname)
        ) {
          return null;
        }

        if (
          isVideoCallTelemetryUrl(getBreadcrumbUrl(breadcrumb)) ||
          containsVideoCallTelemetryReference(breadcrumb)
        ) {
          return null;
        }

        if (isExpectedUnauthorizedBreadcrumb(breadcrumb)) {
          return null;
        }

        return breadcrumb;
      },
    });

    // Lazy-load the replay integration to reduce bundle size
    lazyLoadSentryIntegrations().then((r) => r);
  },
});
